<script setup lang="ts">
import {ref} from 'vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import { useContextualHelp } from '@/composables/useContextualHelp'

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  create: [ticket: { title: string; description: string; files: File[] }]
}>()

useContextualHelp('ticket-create', () => props.isOpen)

const title = ref('')
const description = ref('')
const files = ref<File[]>([])
const loading = ref(false)

function handleClose() {
  resetForm()
  emit('close')
}

function resetForm() {
  title.value = ''
  description.value = ''
  files.value = []
  loading.value = false
}

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files) {
    const newFiles = Array.from(target.files)
    files.value = [...files.value, ...newFiles]
    // Reset the input value so the same file can be selected again if removed
    target.value = ''
  }
}

function removeFile(index: number) {
  files.value.splice(index, 1)
}

async function handleCreate() {
  if (!title.value.trim()) return

  loading.value = true

  try {
    emit('create', {
      title: title.value,
      description: description.value,
      files: files.value,
    })
    resetForm()
  } catch (error) {
    console.error('Error creating ticket:', error)
    loading.value = false
  }
}
</script>

<template>
  <dialog class="modal" :class="{ 'modal-open': isOpen }">
    <div class="modal-box">
      <div class="flex items-center gap-1">
        <h3 class="text-lg font-bold">Création d'un nouveau ticket</h3>
        <HelpButton />
      </div>

      <div class="space-y-4 py-4">
        <div class="w-full">
          <label for="title" class="block text-sm font-medium text-gray-700 mb-1">Sujet *</label>
          <input
            id="title"
            v-model="title"
            placeholder="Sujet du ticket"
            class="input input-bordered w-full"
            :disabled="loading"
          />
        </div>

        <div class="w-full">
          <label for="description" class="block text-sm font-medium text-gray-700 mb-1">Description *</label>
          <textarea
            id="description"
            v-model="description"
            class="textarea textarea-bordered w-full h-32"
            placeholder="Décrivez de manière spécifique votre souci"
            :disabled="loading"
          />
        </div>

        <div class="w-full">
          <label for="files" class="block text-sm font-medium text-gray-700 mb-1">Pièces jointes</label>
          <input
            id="files"
            type="file"
            multiple
            class="file-input file-input-bordered w-full"
            @change="handleFileChange"
            :disabled="loading"
          />
          <div v-if="files.length > 0" class="mt-2 space-y-2">
            <div
              v-for="(file, index) in files"
              :key="index"
              class="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200"
            >
              <span class="text-sm text-gray-700 truncate max-w-[80%]">{{ file.name }}</span>
              <button
                type="button"
                class="btn btn-ghost btn-xs text-error"
                @click="removeFile(index)"
                :disabled="loading"
              >
                Retirer
              </button>
            </div>
            <div class="text-xs text-gray-500">
              {{ files.length }} fichier(s) sélectionné(s)
            </div>
          </div>
        </div>
      </div>

      <div class="modal-action">
        <button
          class="btn"
          @click="handleClose"
          :disabled="loading"
        >Annuler</button>

        <button
          class="btn btn-primary"
          @click="handleCreate"
          :disabled="!title.trim() || !description.trim() || loading"
        >Valider</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="handleClose">close</button>
    </form>
  </dialog>
</template>

<style scoped></style>
